/**
 * HighlightText Component
 * Highlights matching search terms in text with a customizable highlight style.
 * Used in list pages to highlight search matches in table columns.
 */

import React, { useMemo } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { highlightColors } from '@/styles/colors';
import { borderRadiusTokens } from '@/styles/tokens';

export interface HighlightTextProps {
  /** The text to display and potentially highlight (numbers are auto-converted to strings) */
  text: string | number | null | undefined;
  /** The search term to highlight (case-insensitive) */
  searchTerm?: string;
  /** Fallback text when text is empty/null */
  fallback?: React.ReactNode;
  /** Additional className for the container */
  className?: string;
  /** Additional style for the container */
  style?: React.CSSProperties;
}

/**
 * Escapes special regex characters in a string
 */
const escapeRegExp = (string: string): string => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

/**
 * HighlightText component that highlights matching portions of text
 * based on a search term.
 *
 * @example
 * // Basic usage
 * <HighlightText text="Hello World" searchTerm="world" />
 *
 * @example
 * // With fallback
 * <HighlightText text={null} searchTerm="test" fallback="-" />
 *
 * @example
 * // In table column render
 * render: (name: string) => <HighlightText text={name} searchTerm={filters.search} fallback="-" />
 */
export const HighlightText: React.FC<HighlightTextProps> = ({
  text,
  searchTerm,
  fallback = '-',
  className,
  style,
}) => {
  const { mode } = useTheme();
  const highlightBg = mode === 'dark' ? highlightColors.dark : highlightColors.light;

  const highlightedContent = useMemo(() => {
    // Handle empty text (null, undefined, or empty string)
    if (text === null || text === undefined || text === '') {
      return fallback;
    }

    // Convert to string (handles numbers and other types)
    const textString = String(text);

    // If no search term or empty search term, return text as-is
    if (!searchTerm || searchTerm.trim() === '') {
      return textString;
    }

    const trimmedSearch = searchTerm.trim();

    // Create case-insensitive regex for the search term
    const escapedSearch = escapeRegExp(trimmedSearch);
    const regex = new RegExp(`(${escapedSearch})`, 'gi');

    // Split text by the search term
    const parts = textString.split(regex);

    // If no match found, return text as-is
    if (parts.length === 1) {
      return textString;
    }

    // Render with highlighted matches
    return parts.map((part, index) => {
      // Check if this part matches the search term (case-insensitive)
      if (part.toLowerCase() === trimmedSearch.toLowerCase()) {
        return (
          <mark
            key={index}
            style={{
              backgroundColor: highlightBg,
              color: 'inherit',
              fontWeight: 600,
              padding: 0,
              borderRadius: borderRadiusTokens.borderRadiusXS,
            }}
          >
            {part}
          </mark>
        );
      }
      return part;
    });
  }, [text, searchTerm, fallback, highlightBg]);

  return (
    <span className={className} style={style}>
      {highlightedContent}
    </span>
  );
};

export default HighlightText;
